import React, { useState } from "react";
import {
  Breadcrumb,
  EmployeeHeader,
  ProfileTabs,
  SurveyHistory,
  OverviewTab,
  ProfileDetailsTab,
} from "./components";
import { useQuery } from "@tanstack/react-query";
import {
  fetchEmployee,
  fetchSurveyHistory,
  fetchCategoryAnalysis,
} from "../../services/retention";
import { useParams } from "react-router-dom";
import endpoints from "modules/Retention/constants/endpoints";
import { Skeleton } from "@mui/material";
import {
  TabType,
  IconName,
  BreadcrumbItem,
  ContactData,
  WorkData,
  OnboardingData,
  SurveyTimelineItem,
} from "../../types";
import RETENTION_ROUTES from "modules/Retention/constants/routes";
import useUser from "hooks/userHooks";

const EmployeeProfilePage = () => {
  const [activeTab, setActiveTab] = useState<TabType>(TabType.OVERVIEW);
  const { id } = useParams();
  const employeeId = Number(id);
  const { user, isLoading: isLoadingUser } = useUser();

  const { data, isLoading, error } = useQuery({
    queryKey: [endpoints.employee, employeeId],
    queryFn: () => fetchEmployee({ employee_id: employeeId }),
    select: (res) => res?.result?.data,
    enabled: !!employeeId,
    staleTime: 60000,
  });

  const { data: surveyHistoryData, isLoading: surveyHistoryLoading } = useQuery(
    {
      queryKey: [endpoints.surveyHistory, employeeId],
      queryFn: () => fetchSurveyHistory({ employee_id: employeeId }),
      select: (res) => res?.result?.survey_history,
      enabled: !!employeeId,
      staleTime: 180000, // 3 minutes cache
    }
  );

  const { data: categoryAnalysisData, isLoading: categoryAnalysisLoading } =
    useQuery({
      queryKey: [endpoints.categoryAnalysis, employeeId],
      queryFn: () => fetchCategoryAnalysis({ employee_id: employeeId }),
      select: (res) => res?.result?.category_analysis,
      enabled: !!employeeId,
      staleTime: 180000, // 3 minutes cache
    });

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      icon: IconName.FEATHER_HOME,
      label: "Homepage",
      href: RETENTION_ROUTES.HOME,
    },
    {
      icon: IconName.FEATHER_LIST_CHECKS,
      label: "Task Center",
      href: RETENTION_ROUTES.TASK_CENTER,
    },
    {
      icon: IconName.FEATHER_USER,
      label: `${data?.profile.first_name} ${data?.profile.last_name}`,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex h-full w-full max-w-[1280px] flex-col items-start bg-default-background">
        <div className="flex h-16 w-full flex-none items-center gap-2 px-4 md:pl-12 md:pr-6 py-4">
          <Skeleton variant="text" width={200} height={24} />
        </div>
        <div className="flex w-full flex-col items-start gap-4 px-4 md:px-12 py-1">
          <Skeleton variant="rectangular" width="100%" height={200} />
          <Skeleton variant="rectangular" width="100%" height={40} />
        </div>
        <div className="flex w-full max-w-[1280px] grow shrink-0 basis-0 flex-col items-start gap-6 px-4 md:px-12 pt-6 pb-12">
          <div className="flex w-full flex-col items-start gap-4">
            <Skeleton variant="rectangular" width="100%" height={150} />
            <Skeleton variant="rectangular" width="100%" height={200} />
            <Skeleton variant="rectangular" width="100%" height={180} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    console.log("Error loading employee profile:", error);
    return (
      <div className="p-8 text-red-500">Error loading employee profile.</div>
    );
  }

  if (!data) {
    return (
      <div className="flex h-full w-full max-w-[1280px] flex-col items-start bg-default-background">
        <div className="flex h-16 w-full flex-none items-center gap-2 px-4 md:pl-12 md:pr-6 py-4">
          <Skeleton variant="text" width={200} height={24} />
        </div>
        <div className="flex w-full flex-col items-start gap-4 px-4 md:px-12 py-1">
          <Skeleton variant="rectangular" width="100%" height={200} />
          <Skeleton variant="rectangular" width="100%" height={40} />
        </div>
        <div className="flex w-full max-w-[1280px] grow shrink-0 basis-0 flex-col items-start gap-6 px-4 md:px-12 pt-6 pb-12">
          <div className="flex w-full flex-col items-start gap-4">
            <Skeleton variant="rectangular" width="100%" height={150} />
            <Skeleton variant="rectangular" width="100%" height={200} />
            <Skeleton variant="rectangular" width="100%" height={180} />
          </div>
        </div>
      </div>
    );
  }

  const profile = data.profile;

  const contactData: ContactData = {
    firstName: profile.first_name,
    lastName: profile.last_name,
    phone: profile.phone_number,
    email: profile.email,
  };

  const workData: WorkData = {
    location: profile.office_location,
    region: profile.region,
    tenure: profile.tenure,
    hireDate: profile.hired_at,
    supervisor: profile.supervisor,
    employmentType: profile.type,
    employmentStatus: profile.is_active ? "Active" : "At-risk",
  };

  // These would come from API if available
  //   const onboardingData: OnboardingData = {
  //     preferredShifts: ShiftPreference.DAY_SHIFT,
  //     weeklyHours: WeeklyHours.FULL_TIME_70,
  //     careerGoal: "To be an experienced nurse in 2 years",
  //     drivingExpectations: DrivingExpectation.LESS_THAN_3_HOURS,
  //     patientsPreference: PatientPreference.SENIOR_CARE
  //   };

  // These would come from API if available
  const onboardingData = {
    preferredShifts: "N/A",
    weeklyHours: "N/A",
    careerGoal: "N/A",
    drivingExpectations: "N/A",
    patientsPreference: "N/A",
  } as any as OnboardingData;

  const handleEditWork = () => {};
  const handleEditOnboarding = () => {};

  // Employee data for SurveyHistory component
  const employeeData = {
    name:
      profile.first_name && profile.last_name
        ? `${profile.first_name} ${profile.last_name}`
        : null,
    initials:
      profile.first_name && profile.last_name
        ? `${profile.first_name[0]}${profile.last_name[0]}`
        : null,
    status: profile.is_at_risk
      ? "At-risk"
      : profile.is_active
      ? "Active"
      : "Inactive",
    jobTitle: profile.job_title || null,
    location: profile.office_location || null,
    state: profile.region || null,
    hireDate: profile.hired_at || null,
    tenure: profile.tenure || null,
    supervisor: profile.supervisor || null,
    department: profile.job_title || null,
    phone: profile.phone_number || null,
    email: profile.email || null,
    survey_timeline: data.survey_timeline || [],
  };

  return (
    <div className="flex h-full w-full max-w-[1280px] flex-col items-start bg-default-background">
      <Breadcrumb items={breadcrumbItems} />
      <div className="flex w-full flex-col items-start gap-4 px-4 md:px-12 py-1">
        <EmployeeHeader employeeId={employeeId} />
        <ProfileTabs activeTab={activeTab} onTabChange={setActiveTab} />
      </div>
      {activeTab === TabType.OVERVIEW && (
        <OverviewTab
          employeeId={employeeId}
          surveyId={
            data?.survey_timeline?.length > 0
              ? data.survey_timeline.reduce(
                  (latest: SurveyTimelineItem, current: SurveyTimelineItem) =>
                    new Date(current.created_at) > new Date(latest.created_at)
                      ? current
                      : latest
                ).survey_id
              : undefined
          }
          onViewAllSurveyHistory={() => setActiveTab(TabType.SURVEY_HISTORY)}
          employeeData={data}
          categoryAnalysisData={categoryAnalysisData}
          categoryAnalysisLoading={categoryAnalysisLoading}
          surveyHistoryData={surveyHistoryData}
          surveyHistoryLoading={surveyHistoryLoading}
          user={user}
          employee={data}
          isFetchingUser={isLoadingUser}
          isFetchingEmployee={isLoading}
        />
      )}
      {activeTab === TabType.SURVEY_HISTORY && (
        <SurveyHistory
          employeeId={employeeId}
          employeeData={employeeData}
          surveyHistoryData={surveyHistoryData}
          surveyHistoryLoading={surveyHistoryLoading}
          categoryAnalysisData={categoryAnalysisData}
          categoryAnalysisLoading={categoryAnalysisLoading}
        />
      )}
      {activeTab === TabType.ESCALATION_LOGS && (
        <div className="flex w-full max-w-[1280px] bg-neutral-50 grow shrink-0 basis-0 flex-col items-center justify-center px-4 md:px-12 pt-6 pb-12">
          <p className="text-body font-body text-neutral-500">
            Escalation Logs content coming soon...
          </p>
        </div>
      )}
      {activeTab === TabType.PROFILE_DETAILS && (
        <ProfileDetailsTab
          contactData={contactData}
          workData={workData}
          onboardingData={onboardingData}
          employeeId={employeeId}
          onEditWork={handleEditWork}
          onEditOnboarding={handleEditOnboarding}
        />
      )}
    </div>
  );
};

export default EmployeeProfilePage;
